import { useState, useEffect, useRef } from "react";
import "./style.css";
import dict from "../../../Data/dict.json";

import FLOAT_MENU from "../../common_trivial/FLOAT_MENU";

// NEW: Defined the max height as a constant
const MAX_HEIGHT = "300px";

const Icon = () => {
  return (
    <svg
      style={{
        width: "16px",
        height: "16px",
      }}
      aria-hidden="true"
      fill="currentColor"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
    </svg>
  );
};

const Dropdown = ({
  value: propValue,
  children,
  is_multi,
  onChange,
  className = "",
  filter_on,
  placeholder,
  is_enable_empty,
  height, // If you still pass height as a prop, we can map it to maxHeight, or fallback to the constant
  array_value,
  option_style,
  ...props
}) => {
  const language = localStorage?.language ? localStorage?.language : "ina";
  const defaultPlaceholder = placeholder || dict["Select"][language];

  const inputRef = useRef(null);
  const searchRef = useRef(null);

  // eslint-disable-next-line no-unused-vars
  const [filter_text, setFilterText] = useState("");
  const [filtered_children, setFilteredChildren] = useState([]);
  const [is_active, setIsActive] = useState(false);
  const [value, setValue] = useState("");
  const [anchor, setAnchor] = useState(null);

  const convertChildren = (unknown_children) => {
    if (
      typeof unknown_children === "object" &&
      !Array.isArray(unknown_children) &&
      unknown_children !== null
    ) {
      return [unknown_children];
    } else {
      return unknown_children;
    }
  };

  useEffect(() => {
    let selected = [];
    if (is_multi && Array.isArray(propValue)) {
      selected = propValue;
    } else if (!is_multi && typeof propValue !== "object") {
      selected = propValue || "";
    }

    setValue(selected);
    setFilteredChildren(convertChildren(children));
    setFilterText("");
    setIsActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (is_active && searchRef?.current) {
      searchRef?.current?.focus();
      setFilterText("");
    }
  }, [is_active]);

  useEffect(() => {
    if (Array.isArray(propValue) && Array.isArray(value) && is_multi) {
      if (propValue?.length !== value?.length) {
        setValue(propValue);
      }
    } else if (propValue !== value) {
      setValue(propValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue, is_multi]);

  const floatMenuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      // Don't close if clicking inside the input or the float menu
      if (
        (inputRef.current && inputRef.current.contains(e.target)) ||
        (floatMenuRef.current && floatMenuRef.current.contains(e.target))
      ) {
        return;
      }
      setIsActive(false);
    };

    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  const filterOnChange = (e) => {
    const keywords = e?.target?.value?.toLowerCase();
    const regex_string = `${keywords
      ?.split(" ")
      ?.map((word) => `(?=.*?${word})`)
      ?.join("")}.*`;
    const regex_pattern = new RegExp(regex_string);
    if (keywords) {
      const filtered = convertChildren(children).filter((item) => {
        const childContent = item?.props?.children;
        // Convert to string and handle nested children
        const textContent =
          typeof childContent === "string"
            ? childContent
            : typeof childContent === "number"
              ? String(childContent)
              : childContent?.props?.children || "";
        return regex_pattern.test(String(textContent).toLowerCase());
      });
      setFilteredChildren(filtered);
      setFilterText(keywords);
    } else {
      setFilteredChildren(convertChildren(children));
      setFilterText(keywords);
    }
  };

  const toggleDropdown = (event) => {
    setFilteredChildren(convertChildren(children));
    setIsActive(!is_active);
    setAnchor(event?.currentTarget);
  };

  const removeOption = (option) => {
    return value.filter((val) => val !== option);
  };

  const handleClick = (key) => {
    let newValue = "";
    if (is_multi) {
      newValue = key ? [...value, key] : [value];
    } else {
      newValue = key || "";
    }
    setValue(newValue);
    if (onChange) onChange(newValue);

    // Close the menu after selection (only in single-select mode)
    if (!is_multi) {
      setIsActive(false);
    }
  };

  const onTagRemove = (tagKey) => {
    const newValue = removeOption(tagKey);
    setValue(newValue);
    if (onChange) onChange(newValue);
  };

  const isSelected = (key) => {
    if (is_multi) {
      return Array.isArray(value) && value.includes(key);
    } else {
      return value === key;
    }
  };

  // 1. Defined standard ellipsis style to reuse
  const baseItemStyle = {
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-block",
    verticalAlign: "bottom",
  };

  const combinedOptionStyle = {
    ...baseItemStyle,
    ...option_style,
  };

  const options = Array.isArray(filtered_children) && (
    <>
      {is_enable_empty && (
        <div
          key=""
          className={`badge margin_bottom cursor_pointer ${
            !value || (Array.isArray(value) && value.length === 0)
              ? "background_black"
              : "background_grey"
          }`}
          style={combinedOptionStyle}
          onClick={() => {
            handleClick();
          }}
        >
          Select
        </div>
      )}
      {filtered_children?.length > 0 &&
        filtered_children?.map((item, idx) => {
          return (
            <div
              key={idx}
              onClick={() => {
                handleClick(item.key);
              }}
              data-mapid={`selectType-${idx}`}
            >
              <div
                title={
                  typeof item.props.children === "string"
                    ? item.props.children
                    : ""
                }
                style={combinedOptionStyle}
                className={`badge margin_bottom cursor_pointer ${
                  isSelected(item.key) ? "background_black" : "background_grey"
                }`}
              >
                {item.props.children}
              </div>
              <br />
            </div>
          );
        })}
    </>
  );

  const getDisplay = () => {
    if (!value || value?.length === 0) {
      return defaultPlaceholder;
    }
    if (is_multi) {
      if (Array.isArray(value)) {
        return (
          <div className="parent_children_dropdown">
            {value?.length >= 0 &&
              value?.map((key, idx) => {
                const label = filtered_children?.find(
                  (child) => child?.key === key,
                )?.props?.children;
                return (
                  <div
                    key={idx}
                    className="children_dropdown"
                    style={{ ...baseItemStyle, marginRight: "4px" }}
                    onClick={(e) => onTagRemove(key)}
                  >
                    {label}
                  </div>
                );
              })}
          </div>
        );
      } else {
        return defaultPlaceholder;
      }
    } else {
      if (Array.isArray(filtered_children)) {
        const label = filtered_children?.find((child) => child?.key === value)
          ?.props?.children;
        return label?.props?.children || label;
      }
      return defaultPlaceholder;
    }
  };

  // NEW: Using the constant for maxHeight so it can shrink but never exceed the limit
  let object_float = {
    maxHeight: MAX_HEIGHT,
    overflowY: "auto",
  };

  return (
    <main>
      <section
        {...props}
        className={`noselect background_white outline_grey button_big justify_between ${className}`}
        style={{
          display: "flex",
          alignItems: "center",
        }}
        onClick={(event) => toggleDropdown(event)}
        ref={inputRef}
      >
        <span className="dropdown_child " style={baseItemStyle}>
          {getDisplay()}
        </span>
        <span className="flex">{<Icon />}</span>
      </section>

      {is_active && (
        <FLOAT_MENU anchorEl={anchor} open={is_active} onClose={toggleDropdown}>
          <div ref={floatMenuRef}>
            {filter_on && (
              <input
                className="margin_bottom"
                type="text"
                ref={searchRef}
                onChange={filterOnChange}
              />
            )}
            <div className="option_list_box_dropdown" style={object_float}>
              {options}
            </div>
          </div>
        </FLOAT_MENU>
      )}
    </main>
  );
};

export default Dropdown;
